import React, { useEffect, useState, useContext } from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { HelpOutline } from "@mui/icons-material";
import { useParams } from "react-router-dom";
import { useUser } from "../../hook/useUser.js";
import { getInsightApi, getEquipmentTrackingApi, getQualificationTrackingApi, exportPerformanceApi } from "../../api/safety";
import { NotificationContext } from "../../component/popup/NotificationProvider";
import dayjs from "dayjs";
import MainTable from "../../component/table/MainTable.js";
import SelectComponent from "../../component/SelectComponent.js";
import Container from "../../component/Container.js";
import SafetyChartTable from "./components/SafetyChartTable.js";
import SafetyInsight from "./components/SafetyInsight.js";
import TooltipInfo from "../../component/TooltipInfo.js";
import ManageQualificationPopup from "./components/ManageQualificationPopup.js";
import FullScreenLoading from "../../component/FullScreenLoading.js";

const SafetyTooltip = () => {
  const icon = (
    <HelpOutline
      sx={{
        position: "absolute",
        top: "40px",
        left: "104px",
      }}
    />
  );
  const content = (
    <Box sx={{ p: 1 }}>
      For all Incident Report relating to Fatal Injury, Days Away from Works Injury, Restricted Work Injury, Medical Treatment Injury, First Aid
      Treatment or Near Miss, please refer to separate documentation not currently stored within the Project Workplace.
    </Box>
  );
  return <TooltipInfo icon={icon} content={content} sx={{ left: "120px", top: "55px" }} />;
};

const SafetyPage = () => {
  const { projectId } = useParams();
  const { showSuccess, showError } = useContext(NotificationContext);
  const { user } = useUser(projectId);
  const userCompany = user?.company;
  const equipmentTableHead = [
    { id: "company", label: "Company", align: "left" },
    { id: "equipment", label: "Equipment", align: "left" },
    { id: "amount", label: "Amount", align: "left" },
  ];
  const qualificationTableHead = [
    { id: "company", label: "Company", align: "left" },
    { id: "name", label: "Name", align: "left" },
    { id: "qualification", label: "Qualification", align: "left" },
    {
      id: "certificateDate",
      label: "Certificate Date",
      align: "left",
      type: "date",
    },
    {
      id: "expiryDate",
      label: "Certificate Expiry Date",
      align: "left",
      type: "date",
    },
  ];
  const getBottomTabs = (userCompany) => {
    const tabs = [
      {
        title: "Site Overview",
        value: "Overview",
        icon: "assets/icons/safety.svg",
      },
      {
        title: "Site Equipment Tracking",
        value: "Equipment",
        icon: "assets/icons/tracking.svg",
      },
    ];

    if (userCompany && userCompany === "CLP") {
      tabs.push({
        title: "Personnel Qualification Tracking",
        value: "Qualification",
        icon: "assets/icons/personnel.svg",
      });
    }

    return tabs;
  };
  const bottomTabs = getBottomTabs(userCompany);
  const topTabs = [
    {
      title: "Site Overview",
      value: "Overview",
      icon: "assets/icons/safety.svg",
    },
    {
      title: "Confined Space Monitoring",
      value: "Monitoring",
      icon: "assets/icons/monitoring.svg",
    },
  ];
  const titleStyle = {
    width: 280,
    height: 25,
    color: "#fff",
    backgroundSize: "cover",
    display: "flex",
    alignItems: "center",
    pl: 2,
    cursor: "pointer",
    gap: 1,
  };
  const [state, setState] = useState({
    currentTopTab: "Overview",
    currentBottomTab: "Overview",
    TRIRValue: 0,
    LTIRValue: 0,
    headerDateColumns: [],
    performanceTableData: [
      { label: "Fatal", headerDate0: 0, headerDate1: 0, headerDate2: 0 },
      {
        label: "Days Away from Work",
        headerDate0: 0,
        headerDate1: 0,
        headerDate2: 0,
      },
      {
        label: "Restricted Work",
        headerDate0: 0,
        headerDate1: 0,
        headerDate2: 0,
      },
      {
        label: "Medical Treatment",
        headerDate0: 0,
        headerDate1: 0,
        headerDate2: 0,
      },
      {
        label: "First Aid Treatment",
        headerDate0: 0,
        headerDate1: 0,
        headerDate2: 0,
      },
      { label: "Near Miss", headerDate0: 0, headerDate1: 0, headerDate2: 0 },
      {
        label: "Safety Reporting & Observation",
        headerDate0: 0,
        headerDate1: 0,
        headerDate2: 0,
      },
    ],
    isLoading: false,
  });

  const [equipmentData, setEquipmentData] = useState(null);
  const [qualificationData, setQualificationData] = useState(null);
  const [filteredQualificationData, setFilteredQualificationData] = useState([]);
  const [qualificationPopup, setQualificationPopup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyValue, setCompanyValue] = useState([]);
  const getInsightData = async () => {
    setState((prevState) => ({
      ...prevState,
      isLoading: true,
    }));
    try {
      const result = await getInsightApi(projectId);
      if (result) {
        const rawDataMap = new Map(result?.performance.map((item) => [item.type.toLowerCase().replace(/_/g, " "), item]));
        const dateWeek = `Week ${result?.weekNo}\n${dayjs(result?.weekStart).format("DD/M")}-${dayjs(result?.weekEnd).format("DD/M/YYYY")}`;
        const dateMonth = `Month ${result?.monthNo}\n${dayjs(result?.month).format("MMM YYYY")}`;
        const updatedTableData = state.performanceTableData.map((row) => {
          const rawItem = rawDataMap.get(row.label.toLowerCase());
          if (rawItem) {
            return {
              ...row,
              headerDate0: rawItem.week,
              headerDate1: rawItem.month,
              headerDate2: rawItem.projectToDate,
            };
          }
          return row;
        });
        setState((prevState) => {
          const formatValue = (val) => {
            const num = parseFloat(val);
            if (isNaN(num)) return "NA";
            return num.toFixed(2);
          };

          return {
            ...prevState,
            TRIRValue: formatValue(result?.TRIR),
            LTIRValue: formatValue(result?.LDIR),
            headerDateColumns: [dateWeek, dateMonth, "Project to Date"],
            performanceTableData: updatedTableData,
            isLoading: false,
          };
        });
      }
    } catch {
      showError("Received insight data occurred error.");
    }
  };

  const getEquipmentTrackingData = async () => {
    try {
      const result = await getEquipmentTrackingApi(projectId);
      if (result?.data) {
        let filteredData = result.data;
        if (userCompany && userCompany !== "CLP") {
          filteredData = result.data.filter((item) => item.company.includes(userCompany));
        }
        setEquipmentData(filteredData);
      }
    } catch {
      showError("Received equipment data occurred error.");
    }
  };

  const getQualificationTrackingData = async () => {
    try {
      const result = await getQualificationTrackingApi(projectId);
      if (result?.data) {
        const options = result?.data?.map((item) => {
          return {
            value: item.company,
            label: item.company,
          };
        });
        const values = options?.map((item) => item.value);
        setCompanyOptions(options);
        setCompanyValue(values);
        setQualificationData(result?.data);
      }
    } catch {
      showError("Received personnel qualification data occurred error.");
    }
  };

  const handleFileUploadSuccess = () => {
    getQualificationTrackingData();
  };

  const exportPerformanceButton = async () => {
    setIsLoading(true);
    try {
      const result = await exportPerformanceApi(projectId);
      if (result) {
        const data = result?.data;
        const link = document.createElement("a");
        link.href = data;
        link.download = data?.filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        showSuccess("The file has been successfully downloaded.");
      } else {
        showError("Export failed.");
      }
    } catch {
      showError("Export failed.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getInsightData();
    getEquipmentTrackingData();
    getQualificationTrackingData();
  }, [projectId, userCompany]);

  useEffect(() => {
    if (!qualificationData || !Array.isArray(qualificationData)) return;
    if (!companyValue || companyValue.length === 0) {
      setFilteredQualificationData(qualificationData);
      return;
    }
    const filtered = qualificationData.filter((item) => companyValue.includes(item.company));
    setFilteredQualificationData(filtered);
  }, [companyValue, qualificationData]);

  return (
    <>
      <Box display="flex" width="100%" height="100%" gap={2}>
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 620,
          }}
        >
          <Container title="Safety Insight" icon="assets/icons/safety.svg" sx={{ height: "51vh" }} overflow="hidden">
            <SafetyInsight showButton />
          </Container>
          <Container title="Overall Safety Performance" icon="assets/icons/safety.svg" sx={{ height: "37vh" }} overflow="hidden">
            {state.isLoading ? (
              <Box
                sx={{
                  height: 280,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress />
              </Box>
            ) : (
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  width: "100%",
                  height: "100%",
                }}
              >
                <SafetyTooltip />
                <Button
                  onClick={exportPerformanceButton}
                  variant="contained"
                  color="secondary"
                  sx={{
                    pr: 1.5,
                    pl: 1,
                    py: 0,
                    lineHeight: "30px",
                    borderRadius: "20px",
                    position: "absolute",
                    left: "16px",
                    top: "35px",
                    zIndex: 99,
                  }}
                  startIcon={<img src="assets/buttons/export-blue.svg" alt="Export" style={{ height: 20 }} />}
                >
                  Export
                </Button>
                <SafetyChartTable
                  tableData={state.performanceTableData}
                  headerDateColumns={state.headerDateColumns}
                  TRIRValue={state.TRIRValue}
                  LTIRValue={state.LTIRValue}
                />
              </Box>
            )}
          </Container>
        </Box>
        <Box
          sx={{
            flex: 1.3,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            minWidth: 935,
          }}
        >
          <Box
            sx={{
              position: "relative",
              height: "51vh",
              border: "1px solid #ccc",
              borderRadius: "10px",
            }}
          >
            <Box sx={{ display: "flex", position: "absolute", gap: 2, zIndex: 2 }}>
              {topTabs.map((tab) => (
                <Box
                  key={tab.value}
                  sx={{
                    ...titleStyle,
                    backgroundImage: `url(assets/background/${state.currentTopTab === tab.value ? "titlebg" : "greytitlebg"}.svg)`,
                  }}
                  onClick={() => {
                    setState((prevState) => ({
                      ...prevState,
                      currentTopTab: tab.value,
                    }));
                  }}
                >
                  <img src={tab.icon} style={{ height: 20 }} alt="safety" />
                  <Typography component="span">{tab.title}</Typography>
                </Box>
              ))}
            </Box>
            {state.currentTopTab === "Overview" && (
              <Box
                sx={{
                  height: "100%",
                  width: "100%",
                  position: "absolute",
                  top: 0,
                }}
              >
                <img
                  src="assets/illustrations/safety_image1.svg"
                  alt="Overview"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    borderRadius: "10px",
                  }}
                />
              </Box>
            )}
            {state.currentTopTab === "Monitoring" && (
              <Box sx={{ display: "flex", height: "100%", gap: 2 }}>
                <Box sx={{ height: "100%" }}>
                  <img src="assets/illustrations/safety_image2.svg" alt="Monitoring" style={{ height: "100%", borderRadius: "10px" }} />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    pt: 2,
                    gap: 3,
                  }}
                >
                  <img src="assets/illustrations/safety_image3.svg" alt="Monitoring" />
                  <img src="assets/illustrations/safety_image4.svg" alt="Monitoring" />
                  <img src="assets/illustrations/safety_image5.svg" alt="Monitoring" />
                </Box>
              </Box>
            )}
          </Box>
          <Box
            sx={{
              height: "37vh",
              position: "relative",
              border: "1px solid #ccc",
              borderRadius: "10px",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, zIndex: 2 }}>
              {bottomTabs.map((tab) => (
                <Box
                  key={tab.value}
                  sx={{
                    ...titleStyle,
                    backgroundImage: `url(assets/background/${state.currentBottomTab === tab.value ? "titlebg" : "greytitlebg"}.svg)`,
                  }}
                  onClick={() => {
                    setState((prevState) => ({
                      ...prevState,
                      currentBottomTab: tab.value,
                    }));
                  }}
                >
                  <img src={tab.icon} style={{ height: 20 }} alt="safety" />
                  <Typography component="span">{tab.title}</Typography>
                </Box>
              ))}
            </Box>
            {state.currentBottomTab === "Overview" && (
              <Box sx={{ display: "flex", height: "calc(100% - 25px)", gap: 3 }}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 2,
                    pl: 2,
                    pt: 2.5,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 2 }}>
                    <img src="assets/illustrations/safety_image6.svg" alt="Overview" />
                    <img src="assets/illustrations/safety_image7.svg" alt="Overview" />
                  </Box>
                  <img src="assets/illustrations/safety_image8.svg" alt="Overview" />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 4,
                    pt: 2.5,
                  }}
                >
                  <Box sx={{ display: "flex", gap: 3 }}>
                    <img src="assets/illustrations/safety_image9.svg" alt="Overview" />
                    <img src="assets/illustrations/safety_image10.svg" alt="Overview" />
                  </Box>
                  <img src="assets/illustrations/safety_image11.svg" alt="Overview" />
                </Box>
              </Box>
            )}
            {state.currentBottomTab === "Equipment" && (
              <Box sx={{ height: "calc(100% - 25px)", display: "flex", p: 2 }}>
                <MainTable
                  sx={{ maxHeight: "100%", height: "100%" }}
                  columns={equipmentTableHead}
                  rows={equipmentData}
                  headerStyles={{
                    default: { backgroundColor: "#F1F1F1" },
                  }}
                />
              </Box>
            )}
            {state.currentBottomTab === "Qualification" && (
              <Box
                sx={{
                  height: "calc(100% - 25px)",
                  px: 2,
                  pb: 2,
                }}
              >
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", my: 1 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <SelectComponent
                      options={companyOptions}
                      onChange={(value) => setCompanyValue(value)}
                      value={companyValue}
                      isMultiSelect
                      selectLabel="Company"
                    />
                  </Box>
                  <Button
                    onClick={() => setQualificationPopup(true)}
                    variant="contained"
                    color="secondary"
                    sx={{
                      pr: 1.5,
                      pl: 1,
                      py: 0,
                      lineHeight: "30px",
                      borderRadius: "20px",
                    }}
                    startIcon={<img src="assets/buttons/set.svg" alt="Manage Qualification" style={{ height: 20 }} />}
                  >
                    Manage Test & Training
                  </Button>
                </Box>
                <MainTable
                  paperSx={{ height: "calc(100% - 50px)" }}
                  sx={{ maxHeight: "100%", height: "100%" }}
                  columns={qualificationTableHead}
                  rows={filteredQualificationData}
                  headerStyles={{
                    default: { backgroundColor: "#F1F1F1" },
                  }}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Box>
      <ManageQualificationPopup
        open={qualificationPopup}
        onClose={() => setQualificationPopup(false)}
        onUploadSuccess={handleFileUploadSuccess}
        user={user}
      />
      <FullScreenLoading isLoading={isLoading} />
    </>
  );
};
export default SafetyPage;
